import React, { useState, useEffect, useMemo } from 'react';
import { X, Loader2, User, Calendar, AlertTriangle, Sparkles, Copy, Sun, Moon, Clock, ChevronDown, History, BookOpen, CalendarPlus } from 'lucide-react';
import { USER_TYPE_LABELS, assignableRoles } from '../lib/permissions';
import { useAuth } from '../contexts/AuthContext';
import { PROVINCES, communityForProvince } from '../lib/spain';
import {
  listAgreements, listAutonomousCommunities,
  getWorkHours, saveWeekSchedule, DAYS_OF_WEEK, dayOfWeekMeta,
  getPersonAssignmentHistory, assignPersonAgreement,
  getScheduleVersionHistory, createScheduleVersion, addHourBalance,
} from '../services/api';
import { COMPUTATION_START_REASON } from '../lib/hourBalance';
import { isDayKey } from '../lib/workerTime';
import PasswordStrengthMeter from './PasswordStrengthMeter';
import { useToast } from './Toast';
import { FEEDBACK } from '../services/feedbackMessages';
import { isPasswordValid, firstFailureMessage } from '../lib/passwordStrength';
import { agreementsForCompany, isGlobalAgreement } from '../utils/agreements';

const EMPTY = {
  name: '', nif: '', ssNumber: '', email: '', phone: '', password: '',
  type: 0, geoTracking: false, worksRemotely: false,
  // Multicentro: `workingSite` = centro PRIMARIO (compat); `workingSites` = array
  // de todos los centros asignados. El primario siempre está dentro del array.
  workingSite: '', workingSites: [], canRequestCheckins: true, isLoginEnabled: true,
  overtimeAllowed: false,
  enforceStartHour: false,
  // Por defecto true: el trabajador recibe correos de validación GPS de
  // los fichajes de su equipo. Se puede desactivar por trabajador.
  wantsGPSemails: true,
  province: '', locality: '', autonomousCommunity: '', agreement: '',
  // Fecha de inicio de cómputo ("Trabaja desde"): opcional y solo en ALTA.
  // No es un campo de Person (el backend no lo tiene): se guarda tras crear
  // al trabajador como fila marcadora en /api/hourBalance, ver
  // COMPUTATION_START_REASON en src/lib/hourBalance.js.
  computationStart: '',
};

// Por defecto: 5 días laborables turno único 09:00-17:00, fin de semana
// descansa. El admin lo ajusta antes de guardar.
const defaultWeek = () =>
  DAYS_OF_WEEK.map(({ value }) => ({
    dayOfWeek: value,
    // Sábado=6 y Domingo=0 marcados como descanso por defecto
    // (convención backend: 0=Dom, 1=Lun, ..., 6=Sáb).
    isOff: value === 0 || value === 6,
    in1: '09:00', out1: '17:00',
    in2: '', out2: '',
  }));

const trimSecs = (v) => (v ? String(v).slice(0, 5) : '');

const buildWeekFromBackend = (slots) => {
  const byDay = new Map();
  for (const s of (Array.isArray(slots) ? slots : [])) {
    byDay.set(Number(s.dayOfWeek), s);
  }
  return DAYS_OF_WEEK.map(({ value }) => {
    const s = byDay.get(value);
    if (!s) {
      return { dayOfWeek: value, isOff: true, in1: '', out1: '', in2: '', out2: '' };
    }
    // Compatibilidad atras: las filas residuales con in1=out1=00:00 (que el
    // producto antiguo trataba como "horario libre") ahora se importan como
    // descanso. El cleanup definitivo de esas filas se hace via API tras
    // desplegar este cambio.
    const isLegacyFree = trimSecs(s.in1) === '00:00' && trimSecs(s.out1) === '00:00' && !s.in2 && !s.out2;
    if (isLegacyFree) {
      return { dayOfWeek: value, isOff: true, in1: '', out1: '', in2: '', out2: '' };
    }
    return {
      dayOfWeek: value,
      isOff: false,
      in1: trimSecs(s.in1), out1: trimSecs(s.out1),
      in2: trimSecs(s.in2), out2: trimSecs(s.out2),
    };
  });
};

// Auto-promoción al guardar: el backend solo conoce dos slots (in1/out1
// y in2/out2). Si el admin solo rellenó "tarde", lo subimos a in1/out1
// para que el backend lo guarde como turno único. Conservamos el orden
// cronológico si vienen ambos.
const toBackendDay = (day) => {
  if (day.isOff) return { dayOfWeek: day.dayOfWeek, isOff: true };
  const has1 = !!day.in1 && !!day.out1;
  const has2 = !!day.in2 && !!day.out2;
  if (has1 && has2) {
    return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in1, out1: day.out1, in2: day.in2, out2: day.out2 };
  }
  if (has1) {
    return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in1, out1: day.out1, in2: null, out2: null };
  }
  // Solo turno tarde → se promociona al primer slot.
  return { dayOfWeek: day.dayOfWeek, isOff: false, in1: day.in2, out1: day.out2, in2: null, out2: null };
};

// Compara dos jornadas (arrays de día) ignorando orden y considerando que
// si un día está en isOff, el resto de horas no importan. Devuelve true
// si son semánticamente iguales (mismas horas configuradas, mismos días
// de descanso) y false si el admin tocó algo.
const isSameWeek = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  const sortByDow = (arr) => [...arr].sort((x, y) => Number(x.dayOfWeek) - Number(y.dayOfWeek));
  const da = sortByDow(a);
  const db = sortByDow(b);
  for (let i = 0; i < da.length; i += 1) {
    const x = da[i], y = db[i];
    if (Number(x.dayOfWeek) !== Number(y.dayOfWeek)) return false;
    if (!!x.isOff !== !!y.isOff) return false;
    if (x.isOff) continue; // si ambos están en descanso, el resto da igual
    if ((x.in1 || '') !== (y.in1 || '')) return false;
    if ((x.out1 || '') !== (y.out1 || '')) return false;
    if ((x.in2 || '') !== (y.in2 || '')) return false;
    if ((x.out2 || '') !== (y.out2 || '')) return false;
  }
  return true;
};

const WorkerModal = ({ open, companyId, initial, workplaces = [], onClose, onSubmit }) => {
  const isEdit = !!initial?.id;
  const { user: authUser } = useAuth();

  const typeOptions = useMemo(() => {
    const allowed = new Set(assignableRoles(authUser?.type));
    if (isEdit && initial?.type != null) allowed.add(initial.type);
    if (allowed.size === 0) allowed.add(initial?.type ?? 0);
    return Object.entries(USER_TYPE_LABELS)
      .map(([k, v]) => ({ value: Number(k), label: v }))
      .filter((o) => allowed.has(o.value));
  }, [authUser?.type, isEdit, initial?.type]);

  const lockedWorkingSite = authUser?.type === 2 && authUser?.workingSite != null
    ? String(authUser.workingSite)
    : null;

  const [form, setForm] = useState(EMPTY);
  const [week, setWeek] = useState(defaultWeek);
  // Snapshot de la jornada tal como vino del backend (o defaultWeek si es
  // alta). Lo usamos para detectar si el admin la tocó realmente — sin
  // esto, cualquier guardado (incluido tan solo cambiar isLoginEnabled)
  // disparaba el flujo completo de saveWeekSchedule y, con él, DELETEs
  // innecesarios de los días marcados como `isOff` que daban 404 ruidosos
  // en consola. Más importante: por requisito legal NUNCA borramos datos
  // del trabajador si el admin no los modificó explícitamente.
  const [weekSnapshot, setWeekSnapshot] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState(null);
  const toast = useToast();
  const [agreements, setAgreements] = useState([]);
  const [autonomousCommunities, setAutonomousCommunities] = useState([]);
  const [loadingWeek, setLoadingWeek] = useState(false);

  // ── Histórico de convenios (PersonAssignment) ───────────────────────────
  // Solo se carga en edición. Si está vacío (seed no lanzado o trabajador
  // sin asignaciones time-bounded), caemos al campo legacy `worker.agreement`
  // y el select editable funciona como antes.
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  // Estado del mini-formulario "Cambiar convenio a partir de…": cuando es
  // null, no se muestra. Cuando se abre, se inicializa con la fecha de hoy
  // y el último convenio (o el actual del trabajador).
  const [changeAgreement, setChangeAgreement] = useState(null);
  const [savingAgreementChange, setSavingAgreementChange] = useState(false);
  const [agreementChangeError, setAgreementChangeError] = useState(null);

  // Convenios asignables a este trabajador: los de su empresa + los globales.
  // El GET del backend nuevo ya viene acotado para los admins de empresa, pero
  // el superadmin recibe TODOS y el backend antiguo también; filtramos aquí.
  // Si el trabajador tiene ya un convenio (legacy) fuera de ese conjunto lo
  // mantenemos en la lista para que el select no se quede en blanco.
  const agreementOptions = useMemo(() => {
    const scoped = agreementsForCompany(agreements, companyId);
    const current = form.agreement ? Number(form.agreement) : null;
    if (current == null || scoped.some((a) => Number(a.id) === current)) return scoped;
    const extra = agreements.find((a) => Number(a.id) === current);
    return extra ? [...scoped, extra] : scoped;
  }, [agreements, companyId, form.agreement]);

  // ── Histórico de versiones de horario (ScheduleVersion) ─────────────────
  const [scheduleHistory, setScheduleHistory] = useState([]);
  // Modo del editor de jornada cuando hay histórico:
  //   'edit-current' → upsert sobre la versión activa (comportamiento actual)
  //   { mode: 'new', validFrom: 'YYYY-MM-DD' } → crear ScheduleVersion nueva
  //     y enviar los 7 slots dentro de ella.
  const [scheduleEditMode, setScheduleEditMode] = useState('edit-current');
  // El admin modificó conscientemente el campo de contraseña? Lo usamos
  // para no enviar al backend lo que el navegador haya autocompletado en
  // edición (Chrome/Edge rellenan inputs type=password con credenciales
  // guardadas y eso pisaba el hash sin querer).
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Modo de fichaje: 'validation' | 'free' | 'mixed' | null.
  // Es una proyección de tres flags del backend en una sola opción de UI
  // (ver radio group más abajo). Inicializamos a null para forzar al admin
  // a elegir uno conscientemente en alta. En edición, lo deducimos de los
  // flags actuales del trabajador.
  const [fichajeMode, setFichajeModeState] = useState(null);

  // Acordeón de secciones. El admin puede colapsar cualquier bloque para
  // achicar el modal y NO tener que scrollear. Default: todo abierto en
  // alta (para que se vea claro lo que hay que rellenar); en edición
  // dejamos también todo abierto pero el admin puede plegar lo que no
  // quiere tocar. El collapse es por-sección y persiste mientras el modal
  // esté abierto — al reabrir vuelve al default.
  const [collapsed, setCollapsed] = useState({ data: false, schedule: false, mode: false });
  const toggleCollapse = (key) => setCollapsed((c) => ({ ...c, [key]: !c[key] }));
  const expandAll = () => setCollapsed({ data: false, schedule: false, mode: false });
  const collapseAll = () => setCollapsed({ data: true, schedule: true, mode: true });
  const allCollapsed = collapsed.data && collapsed.schedule && collapsed.mode;

  // Deduce el modo desde los flags del backend. Preferimos 'mixed' si
  // enforceStartHour está activo (es la regla más restrictiva); si no, y
  // ambos canRequestCheckins + overtimeAllowed están activos, es 'free';
  // en el resto de casos (configuraciones legacy o conservadoras),
  // 'validation'. Los flags antiguos que no encajen exactamente se
  // recompondrán al guardar siguiendo este mismo mapeo.
  const deduceMode = (worker) => {
    if (!worker) return null;
    if (worker.enforceStartHour) return 'mixed';
    if (worker.canRequestCheckins && worker.overtimeAllowed) return 'free';
    return 'validation';
  };

  // Aplica el modo elegido sobre form: reescribe los tres flags
  // según la tabla mutuamente excluyente acordada con el usuario.
  const setFichajeMode = (mode) => {
    setFichajeModeState(mode);
    setForm((f) => {
      if (mode === 'validation') {
        return { ...f, canRequestCheckins: false, overtimeAllowed: false, enforceStartHour: false };
      }
      if (mode === 'free') {
        return { ...f, canRequestCheckins: true, overtimeAllowed: true, enforceStartHour: false };
      }
      if (mode === 'mixed') {
        return { ...f, canRequestCheckins: false, overtimeAllowed: true, enforceStartHour: true };
      }
      return f;
    });
  };

  useEffect(() => {
    if (!open) return;
    // Catálogo de convenios. Guardamos el listado COMPLETO (sirve para poner
    // nombre a asignaciones históricas) y los selectores usan
    // `agreementOptions`: los de la empresa del trabajador + los globales.
    listAgreements(companyId).then((list) => setAgreements(Array.isArray(list) ? list : [])).catch(() => setAgreements([]));
    listAutonomousCommunities().then((list) => setAutonomousCommunities(Array.isArray(list) ? list : [])).catch(() => setAutonomousCommunities([]));
    // Histórico de convenios y horarios del trabajador. Solo en edición:
    // un trabajador nuevo no tiene asignaciones todavía. Si el backend aún
    // no ha hecho el seed o el endpoint devuelve vacío, mantenemos el flujo
    // legacy (select editable de convenio + editor de jornada estándar).
    if (isEdit && initial?.id) {
      setLoadingHistory(true);
      Promise.allSettled([
        getPersonAssignmentHistory(initial.id),
        getScheduleVersionHistory(initial.id),
      ]).then(([assign, sched]) => {
        setAssignmentHistory(assign.status === 'fulfilled' && Array.isArray(assign.value) ? assign.value : []);
        setScheduleHistory(sched.status === 'fulfilled' && Array.isArray(sched.value) ? sched.value : []);
      }).finally(() => setLoadingHistory(false));
    } else {
      setAssignmentHistory([]);
      setScheduleHistory([]);
    }
    setChangeAgreement(null);
    setAgreementChangeError(null);
    setScheduleEditMode('edit-current');
  }, [open, isEdit, initial?.id, companyId]);

  useEffect(() => {
    if (!open) return;
    const base = initial ? {
      name: initial.name || '',
      nif: initial.nif || '',
      ssNumber: initial.ssNumber || '',
      email: initial.email || '',
      phone: initial.phone || '',
      password: '',
      type: initial.type ?? 0,
      geoTracking: !!initial.geoTracking,
      worksRemotely: !!initial.worksRemotely,
      workingSite: initial.primaryWorkplaceId != null ? String(initial.primaryWorkplaceId)
        : (initial.workingSite != null ? String(initial.workingSite) : ''),
      workingSites: (() => {
        const arr = Array.isArray(initial.workingSites) ? initial.workingSites : null;
        if (arr && arr.length) return arr.map((s) => String(s));
        return initial.workingSite != null ? [String(initial.workingSite)] : [];
      })(),
      canRequestCheckins: initial.canRequestCheckins ?? true,
      isLoginEnabled: initial.isLoginEnabled ?? true,
      overtimeAllowed: initial.overtimeAllowed ?? false,
      enforceStartHour: initial.enforceStartHour ?? false,
      // Default true para usuarios viejos sin el campo en BD: queremos
      // que sigan recibiendo correos hasta que el admin desactive.
      wantsGPSemails: initial.wantsGPSemails ?? true,
      province: initial.province || '',
      locality: initial.locality || '',
      autonomousCommunity: initial.autonomousCommunity || '',
      agreement: initial.agreement != null ? String(initial.agreement) : '',
      // En edición no se toca desde aquí: la fecha vive en la ficha del
      // trabajador (ComputationStartModal), que sabe borrar los marcadores
      // antiguos sin tocar los ajustes manuales.
      computationStart: '',
    } : { ...EMPTY };

    if (lockedWorkingSite) { base.workingSite = lockedWorkingSite; base.workingSites = [lockedWorkingSite]; }
    const allowed = assignableRoles(authUser?.type);
    if (!isEdit && allowed.length > 0 && !allowed.includes(base.type)) {
      base.type = allowed[0];
    }

    setForm(base);
    setErrors({});
    setApiError(null);
    setPasswordTouched(false);
    // Deducimos el modo de fichaje a partir de los flags actuales del
    // trabajador (en edición) o lo dejamos null (en alta — el admin debe
    // elegir uno conscientemente antes de guardar).
    setFichajeModeState(isEdit ? deduceMode(initial) : null);

    if (isEdit && initial?.id) {
      setLoadingWeek(true);
      getWorkHours(initial.id)
        .then((slots) => {
          const built = buildWeekFromBackend(slots);
          setWeek(built);
          setWeekSnapshot(built);
        })
        .catch(() => {
          const fallback = defaultWeek();
          setWeek(fallback);
          setWeekSnapshot(fallback);
        })
        .finally(() => setLoadingWeek(false));
    } else {
      const fresh = defaultWeek();
      setWeek(fresh);
      // En alta no hay snapshot previo en backend — guardar la jornada
      // recién creada SIEMPRE si pasa la validación.
      setWeekSnapshot(null);
    }
  }, [open, initial, isEdit, authUser?.type, lockedWorkingSite]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setNum = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) }));
  const toggle = (k) => () => setForm((f) => ({ ...f, [k]: !f[k] }));

  const onProvinceChange = (e) => {
    const province = e.target.value;
    setForm((f) => {
      const suggested = communityForProvince(province);
      const match = suggested && autonomousCommunities.find((c) => c.name.toLowerCase() === suggested.toLowerCase());
      return {
        ...f,
        province,
        autonomousCommunity: f.autonomousCommunity || (match ? match.name : suggested || ''),
      };
    });
  };

  const updateDay = (dow, patch) =>
    setWeek((prev) => prev.map((d) => d.dayOfWeek === dow ? { ...d, ...patch } : d));

  // Copia la jornada del lunes al RESTO DE DÍAS LABORABLES, entendido como
  // "todos los demás días que no estén marcados como No trabaja". Si un
  // día está como descanso (isOff), se respeta y no se sobrescribe.
  // dayOfWeek=1 corresponde a Lunes en la convención backend (0=Dom..6=Sáb).
  const copyMondayToWeekdays = () => {
    setWeek((prev) => {
      const monday = prev.find((d) => d.dayOfWeek === 1);
      if (!monday) return prev;
      return prev.map((d) => {
        if (d.dayOfWeek === 1) return d;
        if (d.isOff) return d;
        return { ...d, in1: monday.in1, out1: monday.out1, in2: monday.in2, out2: monday.out2 };
      });
    });
  };

  const validateInfo = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Obligatorio';
    if (!form.nif.trim()) e.nif = 'Obligatorio';
    if (!form.email.trim()) e.email = 'Obligatorio';
    if (!isEdit && !form.password.trim()) e.password = 'Obligatorio';
    // Política de fortaleza: si se está poniendo o cambiando una contraseña,
    // debe cumplir las reglas. NO aplicamos en edición si el admin no tocó
    // el campo (passwordTouched=false → mantenemos la contraseña actual).
    const requirePwdCheck = !isEdit || passwordTouched;
    if (requirePwdCheck && form.password.trim() && !isPasswordValid(form.password)) {
      e.password = firstFailureMessage(form.password) || 'Contraseña no válida';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Validación de jornada relajada: cualquiera de los dos turnos (mañana o
  // tarde) puede usarse, los dos, o ninguno (en cuyo caso debe estar marcado
  // como No trabaja). Si rellena UNA hora del turno, tiene que rellenar la
  // otra del mismo turno.
  const validateWeek = () => {
    for (const d of week) {
      if (d.isOff) continue;
      const hasIn1 = !!d.in1, hasOut1 = !!d.out1;
      const hasIn2 = !!d.in2, hasOut2 = !!d.out2;
      const m1Complete = hasIn1 && hasOut1;
      const m1Empty = !hasIn1 && !hasOut1;
      const m2Complete = hasIn2 && hasOut2;
      const m2Empty = !hasIn2 && !hasOut2;

      if (!m1Complete && !m1Empty) {
        return `Turno mañana incompleto en ${dayOfWeekMeta(d.dayOfWeek).label}: rellena entrada y salida o déjalas vacías.`;
      }
      if (!m2Complete && !m2Empty) {
        return `Turno tarde incompleto en ${dayOfWeekMeta(d.dayOfWeek).label}: rellena entrada y salida o déjalas vacías.`;
      }
      if (m1Empty && m2Empty) {
        return `${dayOfWeekMeta(d.dayOfWeek).label}: marca "No trabaja" o rellena al menos un turno.`;
      }
    }
    return null;
  };

  const handleSubmit = async (ev) => {
    ev?.preventDefault?.();
    setApiError(null);

    if (!validateInfo()) return;
    // Modo de fichaje obligatorio: el admin debe haber elegido una de las
    // tres opciones (validación / libre / mixto). En alta esto es siempre
    // null hasta que el admin lo marque; en edición ya viene deducido pero
    // por defensa volvemos a exigirlo si por algún motivo quedó nulo.
    if (!fichajeMode) {
      setApiError('Selecciona un modo de fichaje (Validación de jornada, Fichaje libre o Fichaje mixto).');
      return;
    }
    const weekErr = validateWeek();
    if (weekErr) {
      setApiError(weekErr);
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        companyId,
        type: form.type,
        name: form.name.trim(),
        nif: form.nif.trim().toUpperCase(),
        ssNumber: form.ssNumber.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        geoTracking: form.geoTracking,
        worksRemotely: form.worksRemotely,
        // Multicentro: enviamos el array + el primario como NÚMEROS (así lo
        // muestra la doc y evita ambigüedades string/int en la tabla intermedia
        // PersonWorkplace, con constraint único (personId, workplaceId)).
        // `workingSite` se mantiene = primario por compatibilidad legacy.
        workingSites: (form.workingSites.length ? form.workingSites
          : (form.workingSite ? [form.workingSite] : [])).map(Number),
        primaryWorkplaceId: form.workingSite ? Number(form.workingSite)
          : (form.workingSites[0] != null ? Number(form.workingSites[0]) : null),
        workingSite: form.workingSite ? Number(form.workingSite)
          : (form.workingSites[0] != null ? Number(form.workingSites[0]) : null),
        canRequestCheckins: form.canRequestCheckins,
        isLoginEnabled: form.isLoginEnabled,
        overtimeAllowed: form.overtimeAllowed,
        enforceStartHour: form.enforceStartHour,
        wantsGPSemails: form.wantsGPSemails,
        province: form.province || null,
        locality: form.locality.trim() || null,
        autonomousCommunity: form.autonomousCommunity || null,
        agreement: form.agreement ? Number(form.agreement) : null,
      };
      // En alta (no edición) la password es obligatoria y se envía siempre
      // si tiene valor. En edición sólo si el admin la modificó EXPLÍCITAMENTE
      // — Chrome/Edge autocompletan el campo type=password al abrir el modal
      // con credenciales guardadas y eso pisaba el hash sin querer.
      if (form.password.trim() && (!isEdit || passwordTouched)) payload.password = form.password;

      const saved = await onSubmit(payload);
      const workerId = saved?.id || initial?.id;

      // El trabajador ya se guardó: cerramos el modal SIEMPRE que llegamos
      // hasta aquí. Si la jornada falla en el siguiente paso lo avisamos
      // por alert pero no dejamos el modal colgado bloqueando al admin.
      onClose();

      // Fecha de inicio de cómputo del ALTA: el backend no tiene campo para
      // ella, así que va como fila marcadora en /api/hourBalance (offset 0).
      // Best-effort a propósito: si falla, el trabajador YA está creado y solo
      // avisamos — el admin puede ponerla luego desde su ficha.
      if (!isEdit && workerId && isDayKey(form.computationStart)) {
        try {
          await addHourBalance({
            workerId,
            date: form.computationStart,
            reason: COMPUTATION_START_REASON,
            offset: 0,
          });
        } catch (err) {
          toast.warning(FEEDBACK.computationStartOnCreateFailed(err));
        }
      }

      // Persistencia de la jornada. Dos casos:
      //   A) Modo NUEVA versión: el admin pidió que el cambio entre en
      //      vigor a partir de una fecha futura. Creamos primero la
      //      ScheduleVersion y luego hacemos upsert de los 7 slots —
      //      el backend los enlaza automáticamente a la versión activa
      //      (que es la nueva justo después del POST).
      //   B) Modo EDITAR actual (o legacy): mismo flujo de siempre. Solo
      //      persistimos si el admin tocó algo respecto al snapshot
      //      inicial — así evitamos DELETEs ruidosos sobre días marcados
      //      como descanso cuando solo se cambió otro campo.
      const isNewVersionMode = scheduleEditMode && typeof scheduleEditMode === 'object'
        && scheduleEditMode.mode === 'new'
        && scheduleEditMode.validFrom;
      if (workerId && isNewVersionMode) {
        try {
          await createScheduleVersion({ workerId, validFrom: scheduleEditMode.validFrom });
          await saveWeekSchedule(workerId, week.map(toBackendDay));
        } catch (err) {
          toast.warning(FEEDBACK.workerScheduleVersionFailed(err));
        }
      } else {
        const weekChanged = !weekSnapshot || !isSameWeek(weekSnapshot, week);
        if (workerId && weekChanged) {
          try {
            await saveWeekSchedule(workerId, week.map(toBackendDay));
          } catch (err) {
            toast.warning(FEEDBACK.workerScheduleFailed(err));
          }
        }
      }
    } catch (err) {
      setApiError(err.message || 'Error al guardar el trabajador.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = (key) =>
    `w-full px-3 py-2 border rounded-lg focus:outline-none text-sm transition-colors ${
      errors[key] ? 'border-danger focus:border-danger' : 'border-slate-200 focus:border-primary'
    }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 backdrop-blur-sm p-4">
      <div className="bg-white w-full max-w-6xl rounded-2xl shadow-2xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0 gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold">{isEdit ? 'Editar' : 'Alta'}</div>
            <h2 className="font-display text-lg font-bold text-ink truncate">{isEdit ? 'Editar trabajador' : 'Nuevo trabajador'}</h2>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* Atajo global del acordeón: alterna entre desplegar todo y
                plegar todo. Útil cuando el admin quiere ver el modal entero
                de un vistazo, o achicarlo al máximo para revisar otra app. */}
            <button
              type="button"
              onClick={allCollapsed ? expandAll : collapseAll}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors"
              title={allCollapsed ? 'Desplegar todas las secciones' : 'Plegar todas las secciones'}
            >
              <ChevronDown size={12} className={`transition-transform ${allCollapsed ? '' : 'rotate-180'}`} />
              {allCollapsed ? 'Desplegar todo' : 'Plegar todo'}
            </button>
            <button onClick={onClose} className="text-ink-muted hover:text-ink p-1 rounded-lg hover:bg-slate-100 transition-colors" aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex flex-col min-h-0">
          {/* Único contenedor scrolleable que cubre TODO el contenido del
              modal (datos + jornada + tipo de fichaje + apiError). Header y
              footer quedan fuera y fijos. max-h del modal es 92vh — esto
              respeta la altura de la pantalla en cualquier resolución. */}
          <div className="flex-1 overflow-y-auto min-h-0">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)] divide-y lg:divide-y-0 lg:divide-x divide-slate-200">
            {/* Columna izquierda: datos del trabajador */}
            <section className={collapsed.data ? 'px-6 py-3' : 'px-6 py-5 space-y-4'}>
              <button
                type="button"
                onClick={() => toggleCollapse('data')}
                className="w-full flex items-center justify-between gap-2 text-ink group"
                aria-expanded={!collapsed.data}
              >
                <span className="flex items-center gap-2">
                  <User size={16} className="text-primary" />
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider">Datos del trabajador</h3>
                </span>
                <ChevronDown
                  size={16}
                  className={`text-ink-muted group-hover:text-ink transition-transform ${collapsed.data ? '-rotate-90' : ''}`}
                />
              </button>

              {!collapsed.data && (
              <div className="space-y-4 pt-1">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Nombre completo</label>
                  <input type="text" value={form.name} onChange={set('name')} className={inputCls('name')} autoFocus />
                  {errors.name && <div className="text-xs text-danger mt-1">{errors.name}</div>}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Tipo de usuario</label>
                  <select value={form.type} onChange={setNum('type')} className={inputCls('type')}>
                    {typeOptions.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">NIF</label>
                  <input type="text" value={form.nif} onChange={set('nif')} className={inputCls('nif')} placeholder="12345678A" />
                  {errors.nif && <div className="text-xs text-danger mt-1">{errors.nif}</div>}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Nº Seg. Social</label>
                  <input type="text" value={form.ssNumber} onChange={set('ssNumber')} className={inputCls('ssNumber')} placeholder="281234567890" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
                    Contraseña{isEdit && <span className="text-ink-muted/60 normal-case ml-1">(opc.)</span>}
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={(e) => { setPasswordTouched(true); set('password')(e); }}
                    className={inputCls('password')}
                    placeholder={isEdit ? 'sin cambio' : ''}
                    autoComplete="new-password"
                    autoCorrect="off"
                    spellCheck="false"
                  />
                  {errors.password && <div className="text-xs text-danger mt-1">{errors.password}</div>}
                  {/* Sin checklist para no romper el grid de 3 cols.
                      Si el admin quiere ver detalle, basta con escribir y
                      la barra de fuerza ya guía. */}
                  <PasswordStrengthMeter password={form.password} showChecklist={false} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Email</label>
                  <input type="email" value={form.email} onChange={set('email')} className={inputCls('email')} />
                  {errors.email && <div className="text-xs text-danger mt-1">{errors.email}</div>}
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Teléfono</label>
                  <input type="tel" value={form.phone} onChange={set('phone')} className={inputCls('phone')} placeholder="+34 600 000 000" />
                </div>
              </div>

              {/* Inicio de cómputo de la bolsa. Solo en ALTA: en edición se
                  gestiona desde la ficha del trabajador, que además limpia los
                  marcadores antiguos. Opcional: en blanco, la bolsa cuenta
                  desde el primer fichaje, como siempre. */}
              {!isEdit && (
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
                    Trabaja desde <span className="text-ink-muted/60 normal-case">(opcional)</span>
                  </label>
                  <input
                    type="date"
                    value={form.computationStart}
                    onChange={set('computationStart')}
                    className={`${inputCls('computationStart')} tabular-nums`}
                  />
                  <div className="text-xs text-ink-muted mt-1">
                    Desde esta fecha cuentan sus horas en la bolsa. Lo anterior queda fuera del cómputo.
                    Si la dejas en blanco, se cuenta desde su primer fichaje.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">
                    Sedes{lockedWorkingSite && <span className="text-ink-muted/60 normal-case ml-1">(la tuya)</span>}
                  </label>
                  <div className="rounded-lg border border-slate-200 divide-y divide-slate-100 max-h-44 overflow-y-auto">
                    {workplaces.length === 0 && (
                      <div className="px-3 py-2 text-sm text-ink-muted">Sin sedes</div>
                    )}
                    {workplaces.map((w) => {
                      const id = String(w.id);
                      const checked = form.workingSites.includes(id);
                      const isPrimary = String(form.workingSite) === id;
                      return (
                        <label key={w.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!!lockedWorkingSite}
                            onChange={() => setForm((f) => {
                              const has = f.workingSites.includes(id);
                              const workingSites = has
                                ? f.workingSites.filter((s) => s !== id)
                                : [...f.workingSites, id];
                              // Recalcular el primario: si se quitó el que era
                              // principal, pasa al primero que quede; si se añade
                              // y no había ninguno, ese pasa a ser el principal.
                              let workingSite = String(f.workingSite || '');
                              if (has && workingSite === id) workingSite = workingSites[0] || '';
                              if (!has && !workingSite) workingSite = id;
                              return { ...f, workingSites, workingSite };
                            })}
                            className="rounded border-slate-300 text-primary focus:ring-primary"
                          />
                          <span className="flex-1 truncate">{w.name}</span>
                          {checked && (isPrimary ? (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-primary-darker bg-primary-soft px-1.5 py-0.5 rounded shrink-0">Principal</span>
                          ) : (
                            <button
                              type="button"
                              disabled={!!lockedWorkingSite}
                              onClick={() => setForm((f) => ({ ...f, workingSite: id }))}
                              className="text-[10px] uppercase tracking-wider text-ink-muted hover:text-primary-darker underline shrink-0"
                            >
                              Hacer principal
                            </button>
                          ))}
                        </label>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2 inline-flex items-center gap-1.5">
                    <BookOpen size={11} /> Convenio
                    {loadingHistory && <Loader2 size={10} className="animate-spin text-ink-muted/60" />}
                  </label>
                  {assignmentHistory.length === 0 ? (
                    // Modo LEGACY: select editable que se envía en payload.agreement.
                    // Se usa para trabajadores nuevos o cuando el seed no se ha
                    // lanzado en backend todavía. Comportamiento idéntico al anterior.
                    <select value={form.agreement} onChange={set('agreement')} className={inputCls('agreement')}>
                      <option value="">Sin convenio</option>
                      {agreementOptions.map((a) => (
                        <option key={a.id} value={a.id}>{a.name}{isGlobalAgreement(a) ? ' (global)' : ''}</option>
                      ))}
                    </select>
                  ) : (
                    // Modo TIME-BOUNDED: el convenio activo es read-only y se
                    // cambia mediante el endpoint `assignPersonAgreement` (que
                    // cierra automáticamente el anterior). El select del form
                    // ya no se manda al backend en el payload del PUT — el
                    // campo legacy se ignora una vez hay historial.
                    <ActiveAgreementPicker
                      assignmentHistory={assignmentHistory}
                      agreements={agreements}
                      options={agreementOptions}
                      workerId={initial?.id}
                      changeState={changeAgreement}
                      onOpenChange={setChangeAgreement}
                      saving={savingAgreementChange}
                      error={agreementChangeError}
                      onSave={async (payload) => {
                        setAgreementChangeError(null);
                        setSavingAgreementChange(true);
                        try {
                          await assignPersonAgreement(payload);
                          toast.success(FEEDBACK.agreementAssigned({
                            agreementName: agreements.find((a) => Number(a.id) === Number(payload.agreementId))?.name,
                            workerName: initial?.name,
                            validFrom: payload.validFrom,
                          }));
                          const next = await getPersonAssignmentHistory(payload.personId);
                          setAssignmentHistory(Array.isArray(next) ? next : []);
                          setChangeAgreement(null);
                        } catch (err) {
                          setAgreementChangeError(err?.message || 'No se pudo cambiar el convenio.');
                          // Convenio de otra empresa (404 del backend nuevo):
                          // además del error en línea, toast bien visible.
                          if (err?.reason === 'agreement-not-in-company') toast.error(err);
                        } finally {
                          setSavingAgreementChange(false);
                        }
                      }}
                    />
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Provincia</label>
                  <select value={form.province} onChange={onProvinceChange} className={inputCls('province')}>
                    <option value="">—</option>
                    {PROVINCES.map((p) => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">Localidad</label>
                  <input type="text" value={form.locality} onChange={set('locality')} className={inputCls('locality')} placeholder="Municipio" />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider text-ink-muted mb-2">CCAA</label>
                  <select value={form.autonomousCommunity} onChange={set('autonomousCommunity')} className={inputCls('autonomousCommunity')}>
                    <option value="">—</option>
                    {autonomousCommunities.map((c) => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2.5 pt-1">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.isLoginEnabled} onChange={toggle('isLoginEnabled')} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" />
                  <div>
                    <div className="text-sm font-semibold text-ink">Trabajador activo</div>
                    <div className="text-xs text-ink-muted">Si se desactiva, el trabajador no podrá iniciar sesión ni fichar.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.geoTracking} onChange={toggle('geoTracking')} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" />
                  <div>
                    <div className="text-sm font-semibold text-ink">Requiere ubicación GPS al fichar</div>
                    <div className="text-xs text-ink-muted">Solo aplica en mobile: si está activado, valida que el fichaje se hace dentro del radio del centro de trabajo.</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.worksRemotely} onChange={toggle('worksRemotely')} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" />
                  <div>
                    <div className="text-sm font-semibold text-ink">Trabajo remoto</div>
                    <div className="text-xs text-ink-muted">El trabajador trabaja fuera de la oficina.</div>
                  </div>
                </label>
                {/* Solo aplica a roles que SUPERVISAN a otros trabajadores:
                    admin empresa (1) y admin sede (2). El resto de roles
                    no tienen equipo del que recibir alertas GPS. */}
                {(Number(form.type) === 1 || Number(form.type) === 2) && (
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form.wantsGPSemails} onChange={toggle('wantsGPSemails')} className="w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary" />
                    <div>
                      <div className="text-sm font-semibold text-ink">Recibir avisos por email de validación GPS</div>
                      <div className="text-xs text-ink-muted">
                        Cuando un trabajador de su equipo ficha fuera del radio permitido de la sede, este admin recibe un correo de aviso. Por defecto activado.
                      </div>
                    </div>
                  </label>
                )}
              </div>
              </div>
              )}
            </section>

            {/* Columna derecha: jornada laboral */}
            <section className={collapsed.schedule ? 'px-6 py-3 bg-slate-50/50' : 'px-6 py-5 space-y-3 bg-slate-50/50'}>
              <div className="flex items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={() => toggleCollapse('schedule')}
                  className="flex items-center gap-2 text-ink group"
                  aria-expanded={!collapsed.schedule}
                >
                  <Calendar size={16} className="text-primary" />
                  <h3 className="font-display text-sm font-bold uppercase tracking-wider">Jornada laboral</h3>
                  <ChevronDown
                    size={16}
                    className={`text-ink-muted group-hover:text-ink transition-transform ${collapsed.schedule ? '-rotate-90' : ''}`}
                  />
                </button>
                {!collapsed.schedule && (
                  <button
                    type="button"
                    onClick={copyMondayToWeekdays}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold text-primary border border-primary/30 hover:bg-primary-soft transition-colors"
                    title="Copia el horario del lunes al resto de días laborables (los marcados como descanso no se tocan)"
                  >
                    <Copy size={12} /> Mismo horario al resto de días laborables
                  </button>
                )}
              </div>
              {!collapsed.schedule && (
              <div className="space-y-3">

              {/* Selector del modo de edición de la jornada cuando hay
                  histórico de ScheduleVersion. Sin histórico: comportamiento
                  legacy (upsert sobre los workhours actuales). Con histórico:
                  el admin elige si edita la versión activa o crea una nueva
                  versión desde una fecha futura (p.ej. cambio de horario de
                  38h/sem a 40h/sem a partir del 1-jul). El POST de slots se
                  ejecuta DESPUÉS de POST scheduleVersion, así los slots
                  quedan automáticamente enlazados a la versión nueva. */}
              {scheduleHistory.length > 0 && (
                <ScheduleEditModeSelector
                  scheduleHistory={scheduleHistory}
                  editMode={scheduleEditMode}
                  onChange={setScheduleEditMode}
                />
              )}

              <div className="flex items-start gap-2.5 bg-primary-soft border border-primary-light rounded-xl p-2.5">
                <Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
                <div className="text-[11px] text-ink-muted leading-relaxed">
                  Cada día permite turno mañana, turno tarde, ambos o ninguno (No trabaja).
                  Si solo trabaja por la tarde, deja la mañana en blanco.
                </div>
              </div>

              {loadingWeek && (
                <div className="text-xs text-ink-muted flex items-center gap-2">
                  <Loader2 size={12} className="animate-spin" /> Cargando jornada actual…
                </div>
              )}

              <div className="space-y-2">
                {week.map((d) => {
                  const meta = dayOfWeekMeta(d.dayOfWeek);
                  return (
                    <div
                      key={d.dayOfWeek}
                      className={`border rounded-xl p-2.5 transition-colors ${
                        d.isOff ? 'bg-slate-100 border-slate-200' : 'bg-white border-slate-200'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                        <div className="flex items-center gap-2 min-w-[100px]">
                          <span className="font-semibold text-ink text-sm">{meta.label}</span>
                          {d.isOff && <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-slate-300 text-ink">No trabaja</span>}
                        </div>
                        <div className="flex gap-2">
                          <label className="inline-flex items-center gap-1 cursor-pointer text-[11px] text-ink">
                            <input
                              type="checkbox"
                              checked={d.isOff}
                              onChange={(e) => updateDay(d.dayOfWeek, { isOff: e.target.checked })}
                              className="w-3 h-3 rounded border-slate-300 text-primary focus:ring-primary"
                            />
                            No trabaja
                          </label>
                        </div>
                      </div>

                      {!d.isOff && (
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-amber-50/60 border border-amber-100 rounded-lg p-2">
                            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1.5">
                              <Sun size={10} /> Mañana <span className="text-ink-muted/60 font-normal normal-case">(opcional)</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5">
                              <input type="time" value={d.in1} onChange={(e) => updateDay(d.dayOfWeek, { in1: e.target.value })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-primary bg-white" placeholder="Entrada" />
                              <input type="time" value={d.out1} onChange={(e) => updateDay(d.dayOfWeek, { out1: e.target.value })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-primary bg-white" placeholder="Salida" />
                            </div>
                          </div>
                          <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-2">
                            <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-700 mb-1.5">
                              <Moon size={10} /> Tarde <span className="text-ink-muted/60 font-normal normal-case">(opcional)</span>
                            </div>
                            <div className="grid grid-cols-2 gap-1.5">
                              <input type="time" value={d.in2} onChange={(e) => updateDay(d.dayOfWeek, { in2: e.target.value })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-primary bg-white" placeholder="Entrada" />
                              <input type="time" value={d.out2} onChange={(e) => updateDay(d.dayOfWeek, { out2: e.target.value })} className="w-full px-1.5 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:border-primary bg-white" placeholder="Salida" />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              </div>
              )}
            </section>
          </div>
          </div>

          {/* SECCIÓN FIJA — fuera del scroll. "Tipo de fichaje" es el campo
              obligatorio crítico del modal: tiene que estar SIEMPRE a la
              vista para que el admin no se olvide de marcarlo y no tenga
              que scrollear hasta el fondo para verlo. Se mantiene
              colapsable (toggleCollapse('mode')) para no robar altura
              cuando ya está elegido — al plegar se queda una barra fina
              con el modo seleccionado bien visible. Mapeo a flags:
                Validación de jornada → cR=false, oA=false, eSH=false
                Fichaje libre        → cR=true,  oA=true,  eSH=false
                Fichaje mixto        → cR=false, oA=true,  eSH=true   */}
          <section className={`shrink-0 border-t border-slate-200 bg-slate-50/50 ${collapsed.mode ? 'px-6 py-2.5' : 'px-6 py-4'}`}>
            <button
              type="button"
              onClick={() => toggleCollapse('mode')}
              className="w-full flex items-center justify-between gap-2 text-ink group"
              aria-expanded={!collapsed.mode}
            >
              <span className="flex items-center gap-2">
                <Clock size={16} className="text-primary" />
                <h3 className="font-display text-sm font-bold uppercase tracking-wider">Tipo de fichaje</h3>
                {/* En el header colapsado mostramos el modo elegido para
                    que el admin sepa de un vistazo qué está seleccionado
                    sin tener que desplegar. Si falta, lo marcamos en
                    naranja para que llame la atención. */}
                <span className={`text-[11px] font-semibold normal-case ml-1 ${fichajeMode ? 'text-primary-darker' : 'text-amber-600'}`}>
                  {fichajeMode === 'validation' && '— Validación de jornada'}
                  {fichajeMode === 'free' && '— Fichaje libre'}
                  {fichajeMode === 'mixed' && '— Fichaje mixto'}
                  {!fichajeMode && '— elige una opción (obligatorio)'}
                </span>
              </span>
              <ChevronDown
                size={16}
                className={`text-ink-muted group-hover:text-ink transition-transform ${collapsed.mode ? '-rotate-90' : ''}`}
              />
            </button>
            {!collapsed.mode && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5 mt-3">
              {[
                {
                  id: 'validation',
                  title: 'Validación de jornada',
                  desc: 'Solo puede firmar dentro de su turno, sin horas extra ni fichaje en diferido.',
                },
                {
                  id: 'free',
                  title: 'Fichaje libre',
                  desc: 'Puede fichar en diferido. Lo trabajado fuera del turno cuenta como hora extra.',
                },
                {
                  id: 'mixed',
                  title: 'Fichaje mixto',
                  desc: 'No puede fichar antes del turno; al final sí cuentan las horas extra.',
                },
              ].map((opt) => {
                const checked = fichajeMode === opt.id;
                return (
                  <button
                    type="button"
                    key={opt.id}
                    onClick={() => setFichajeMode(opt.id)}
                    className={`text-left p-3 rounded-lg border-2 transition-all ${
                      checked
                        ? 'border-primary bg-primary-soft/40 shadow-sm'
                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span
                        className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded-full border-2 shrink-0 transition-colors ${
                          checked ? 'border-primary bg-primary' : 'border-slate-300 bg-white'
                        }`}
                      >
                        {checked && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-bold leading-tight ${checked ? 'text-primary-darker' : 'text-ink'}`}>
                          {opt.title}
                        </div>
                        <div className="text-[11px] text-ink-muted leading-snug mt-0.5">
                          {opt.desc}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            )}
          </section>

          {apiError && (
            <div className="shrink-0 mx-6 mt-2 mb-2 text-xs text-danger bg-danger/5 border border-danger/20 rounded-lg px-3 py-2 flex items-start gap-2">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <div>{apiError}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 shrink-0">
            <button type="button" onClick={onClose} disabled={submitting} className="px-4 py-2 rounded-lg text-sm font-semibold text-ink-muted hover:bg-slate-100 transition-colors disabled:opacity-50">
              Cancelar
            </button>
            <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60">
              {submitting && <Loader2 size={14} className="animate-spin" />}
              {isEdit ? 'Guardar cambios' : 'Crear trabajador'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// ScheduleEditModeSelector: dos modos cuando hay histórico de versiones.
// · 'edit-current' → upsert sobre la versión activa (comportamiento clásico).
// · { mode: 'new', validFrom } → crea ScheduleVersion nueva al guardar.
//
// Mostramos también un resumen ultracorto de la versión activa (validFrom)
// para que el admin sepa qué está editando.
// ─────────────────────────────────────────────────────────────────────────
const ScheduleEditModeSelector = ({ scheduleHistory, editMode, onChange }) => {
  const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const active = useMemo(() => {
    const open = (scheduleHistory || []).filter((v) => !v.validTo);
    if (open.length > 0) {
      return [...open].sort((a, b) => String(b.validFrom || '').localeCompare(String(a.validFrom || '')))[0];
    }
    return null;
  }, [scheduleHistory]);

  const isNew = typeof editMode === 'object' && editMode?.mode === 'new';
  const newFrom = isNew ? editMode.validFrom : todayIso();

  return (
    <div className="border border-slate-200 rounded-lg bg-white p-2.5 space-y-2">
      <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-1.5">
        <History size={10} /> Modo de edición
      </div>
      <div className="flex flex-col sm:flex-row gap-1.5">
        <button
          type="button"
          onClick={() => onChange('edit-current')}
          className={`flex-1 text-left rounded-md border-2 px-2.5 py-1.5 transition-colors ${
            !isNew ? 'border-primary bg-primary-soft/50' : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >
          <div className={`text-xs font-bold ${!isNew ? 'text-primary-darker' : 'text-ink'}`}>
            Editar versión actual
          </div>
          <div className="text-[10px] text-ink-muted leading-tight">
            Modifica los slots de la versión vigente (desde {active?.validFrom || '—'}).
          </div>
        </button>
        <button
          type="button"
          onClick={() => onChange({ mode: 'new', validFrom: newFrom })}
          className={`flex-1 text-left rounded-md border-2 px-2.5 py-1.5 transition-colors ${
            isNew ? 'border-primary bg-primary-soft/50' : 'border-slate-200 bg-white hover:border-slate-300'
          }`}
        >
          <div className={`text-xs font-bold ${isNew ? 'text-primary-darker' : 'text-ink'}`}>
            Crear nueva versión
          </div>
          <div className="text-[10px] text-ink-muted leading-tight">
            La versión actual se cerrará y la nueva empezará en la fecha indicada.
          </div>
        </button>
      </div>
      {isNew && (
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end pt-1 border-t border-slate-100">
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">
              Vigente desde
            </label>
            <input
              type="date"
              value={editMode.validFrom}
              onChange={(e) => onChange({ mode: 'new', validFrom: e.target.value })}
              className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary"
            />
          </div>
          <div className="text-[10px] text-ink-muted leading-tight sm:max-w-[200px]">
            Al guardar, los slots de abajo se aplicarán a la nueva versión a partir de esa fecha.
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// ActiveAgreementPicker: pieza autocontenida para el caso multi-convenio.
// Muestra el convenio activo del trabajador (extraído del historial) y
// permite cambiarlo a partir de una fecha. El backend cierra solo la
// asignación anterior (validTo = día previo al nuevo validFrom).
//
// Props:
//   · assignmentHistory: ScheduleVersion[] del trabajador (no vacío).
//   · agreements: catálogo completo (para nombrar asignaciones históricas).
//   · options: convenios asignables (empresa del trabajador + globales);
//     si no llega, el selector usa `agreements`.
//   · workerId: id del trabajador (para mandarlo al endpoint).
//   · changeState: { agreementId, validFrom } | null — null = formulario cerrado.
//   · onOpenChange: setter del estado del formulario inline.
//   · saving / error: feedback visual durante el POST.
//   · onSave: callback con el payload listo para mandar.
// ─────────────────────────────────────────────────────────────────────────
const ActiveAgreementPicker = ({
  assignmentHistory, agreements, options, workerId, changeState, onOpenChange, saving, error, onSave,
}) => {
  const selectable = options || agreements || [];
  const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  // Workaround del backend: GET /api/personAssignment/:id/active y /history
  // NO joinean los campos del agreement (assignAgreementName, assignMonthlyHours,
  // assignVacationDays, assignHolidayType). La doc dice que deberían, pero
  // la implementación actual los omite. Hasta que el backend lo arregle,
  // resolvemos el nombre + atributos del agreement desde el catálogo
  // `agreements` que ya tenemos cargado. Si más adelante el backend joinea,
  // los campos ya vienen y este enrich se vuelve no-op.
  const enrich = (entry) => {
    if (!entry) return entry;
    if (entry.assignAgreementName) return entry;
    const ag = (agreements || []).find((a) => Number(a.id) === Number(entry.agreementId));
    if (!ag) return entry;
    return {
      ...entry,
      assignAgreementName: ag.name,
      assignMonthlyHours: ag.monthlyHours,
      assignVacationDays: ag.vacationDays,
      assignHolidayType: ag.holidayType,
    };
  };

  // Active = el que no tiene validTo. Si por error hay varios, cogemos el
  // de validFrom más reciente.
  const active = useMemo(() => {
    const open = (assignmentHistory || []).filter((a) => !a.validTo);
    if (open.length > 0) {
      return enrich([...open].sort((a, b) => String(b.validFrom || '').localeCompare(String(a.validFrom || '')))[0]);
    }
    return enrich([...(assignmentHistory || [])].sort((a, b) => String(b.validFrom || '').localeCompare(String(a.validFrom || '')))[0] || null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentHistory, agreements]);

  // Histórico cerrado, en orden cronológico inverso, para el listado read-only.
  const past = useMemo(
    () => (assignmentHistory || [])
      .filter((a) => a.id !== active?.id)
      .sort((a, b) => String(b.validFrom || '').localeCompare(String(a.validFrom || '')))
      .map(enrich),
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignmentHistory, active, agreements]
  );

  const isOpen = !!changeState;
  const draft = changeState || { agreementId: active?.agreementId ?? '', validFrom: todayIso() };

  return (
    <div className="space-y-2">
      <div className="px-3 py-2 rounded-lg border-2 border-primary/30 bg-primary-soft/30 flex items-center justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="text-sm font-bold text-primary-darker truncate">
            {active?.assignAgreementName || 'Sin convenio activo'}
          </div>
          <div className="text-[10px] text-ink-muted tabular-nums">
            Vigente desde {active?.validFrom || '—'}
            {/* Horas/mes del convenio NO se muestra: el cómputo real sale de la
                jornada semanal del trabajador (suma de lunes a domingo de su
                horario), no del campo monthlyHours del convenio. */}
            {active?.assignVacationDays != null ? ` · ${active.assignVacationDays} días vac.` : ''}
          </div>
        </div>
        {!isOpen && (
          <button
            type="button"
            onClick={() => onOpenChange({ agreementId: active?.agreementId ?? '', validFrom: todayIso() })}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-white border border-primary/30 text-primary-darker hover:bg-primary-soft transition-colors"
          >
            <CalendarPlus size={11} /> Cambiar desde…
          </button>
        )}
      </div>

      {isOpen && (
        <div className="border border-slate-200 rounded-lg bg-white p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold">
            Cambiar convenio
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Vigente desde</label>
              <input
                type="date"
                value={draft.validFrom}
                onChange={(e) => onOpenChange({ ...draft, validFrom: e.target.value })}
                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted mb-1">Nuevo convenio</label>
              <select
                value={draft.agreementId || ''}
                onChange={(e) => onOpenChange({ ...draft, agreementId: e.target.value ? Number(e.target.value) : '' })}
                className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary bg-white"
              >
                <option value="">— elige —</option>
                {selectable.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}{isGlobalAgreement(a) ? ' (global)' : ''}</option>
                ))}
              </select>
            </div>
          </div>
          {error && (
            <div className="text-[11px] text-danger inline-flex items-center gap-1">
              <AlertTriangle size={11} /> {error}
            </div>
          )}
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              onClick={() => onOpenChange(null)}
              disabled={saving}
              className="px-2.5 py-1 rounded-md text-[11px] font-semibold text-ink-muted hover:bg-slate-100"
            >
              Cancelar
            </button>
            <button
              type="button"
              disabled={saving || !draft.agreementId || !draft.validFrom}
              onClick={() => onSave({
                personId: Number(workerId),
                agreementId: Number(draft.agreementId),
                validFrom: draft.validFrom,
              })}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold bg-primary text-white hover:bg-primary-dark disabled:opacity-60"
            >
              {saving && <Loader2 size={10} className="animate-spin" />}
              Aplicar cambio
            </button>
          </div>
        </div>
      )}

      {past.length > 0 && (
        <details className="text-[11px]">
          <summary className="cursor-pointer text-ink-muted hover:text-ink inline-flex items-center gap-1">
            <History size={10} /> Histórico ({past.length})
          </summary>
          <ul className="mt-1.5 space-y-1">
            {past.map((p) => (
              <li key={p.id} className="text-[11px] text-ink-muted flex items-center justify-between gap-2 px-2 py-1 rounded bg-slate-50">
                <span className="truncate font-medium text-ink">{p.assignAgreementName || `Convenio #${p.agreementId}`}</span>
                <span className="tabular-nums shrink-0">{p.validFrom} → {p.validTo || 'abierto'}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default WorkerModal;
